import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Icon } from '@components/icons';
import {
  CaseStudy,
  Reveal,
  ProjectHeader,
  TechChips,
  SectionHeading,
  Note,
  Caption,
  TechList,
  Gallery,
  CaremateArchitecture,
  CarematePipeline,
} from '@components/casestudy';
import search from '@images/caremate/search.png';
import chat from '@images/caremate/chat.png';
import profile from '@images/caremate/profile.png';
import landing from '@images/caremate/landing.png';

const MEDIUM_URL =
  'https://abdoumata.medium.com/caremate-building-a-scalable-microservices-platform-for-personalized-educational-support-nodejs-1c8a69004578';

const StyledLinkButton = styled.a`
  ${({ theme }) => theme.mixins.smallButton};
  display: inline-flex;
  align-items: center;
  gap: 10px;
  margin-top: 22px;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const CarematePage = ({ location }) => (
  <CaseStudy location={location} title="Caremate">
    <Reveal>
      <ProjectHeader
        eyebrow="Projet pluridisciplinaire · Chef d’équipe"
        title="Caremate"
        tagline="Plateforme microservices"
        meta={['Janvier → Juin 2024', 'Mise en relation parents / AVS']}>
        <TechChips
          items={[
            { label: 'Node.js / NestJS', hot: true },
            { label: 'TypeScript', hot: true },
            { label: 'API REST', hot: true },
            { label: 'Next.js' },
            { label: 'RabbitMQ' },
            { label: 'Socket.IO' },
            { label: 'MongoDB' },
            { label: 'Docker' },
            { label: 'Kubernetes (GKE)' },
            { label: 'CI/CD' },
          ]}
        />
      </ProjectHeader>
    </Reveal>

    <Reveal>
      <SectionHeading>Le projet</SectionHeading>
      <p className="cs-lead">
        Caremate met en relation des parents d’enfants autistes avec des{' '}
        <strong>auxiliaires de vie scolaire qualifiés</strong> : recherche géolocalisée et filtrée,
        messagerie, réservation, notifications. J’ai dirigé l’équipe et conçu l’architecture, dans le
        cadre d’un projet pluridisciplinaire en école d’ingénieur.
      </p>
    </Reveal>

    <Reveal>
      <SectionHeading>L’architecture</SectionHeading>
      <p className="cs-lead">
        Le domaine est découpé en services autonomes, chacun avec sa base. Un{' '}
        <strong>API Gateway</strong> centralise l’entrée et l’authentification, un registre de
        services (Eureka) évite de coder en dur les adresses, et <strong>RabbitMQ</strong> porte les
        échanges asynchrones.
      </p>

      <CaremateArchitecture />
      <Caption>
        <b>L’architecture du projet.</b> RabbitMQ découple complètement les notifications :{' '}
        <code>ms-booking</code> publie un événement sans savoir qui l’écoute.
      </Caption>

      <StyledLinkButton href={MEDIUM_URL} target="_blank" rel="noreferrer">
        <Icon name="External" /> Lire l’article technique sur Medium
      </StyledLinkButton>
    </Reveal>

    <Reveal>
      <SectionHeading>Les services que j’ai conçus et développés</SectionHeading>
      <ul className="fancy-list">
        <li>
          <strong>Authentification &amp; profils</strong> : comptes, rôles (parent / auxiliaire),
          contrôle d’accès basé sur les rôles.
        </li>
        <li>
          <strong>Recherche &amp; filtrage</strong> : par localisation, compétences, expérience,
          disponibilité.
        </li>
        <li>
          <strong>Communication</strong> : messagerie temps réel (Socket.IO).
        </li>
        <li>
          <strong>Réservation</strong> : créneaux et gestion des conflits d’agenda.
        </li>
        <li>
          <strong>Notifications</strong> : abonné aux événements RabbitMQ, découplé du reste.
        </li>
      </ul>
    </Reveal>

    <Reveal>
      <SectionHeading>Le produit</SectionHeading>
      <Gallery
        columns={2}
        columnsSm={1}
        items={[
          {
            src: search,
            alt: 'Caremate, recherche et filtres',
            ratio: '16 / 9',
            caption: (
              <>
                <b>Recherche &amp; filtres.</b> Filtrage combiné avec compteurs et tri. Front Next.js
                / TypeScript.
              </>
            ),
          },
          {
            src: chat,
            alt: 'Caremate, messagerie temps réel',
            ratio: '16 / 9',
            caption: (
              <>
                <b>Messagerie temps réel.</b> Conversations parent, auxiliaire via Socket.IO, avec la
                réservation dans le même écran.
              </>
            ),
          },
          {
            src: profile,
            alt: 'Caremate, profil et disponibilité',
            ratio: '16 / 9',
            caption: (
              <>
                <b>Profil &amp; disponibilité.</b> CV, diplômes, compétences et bascule de
                disponibilité.
              </>
            ),
          },
          {
            src: landing,
            alt: 'Caremate, site public',
            ratio: '16 / 9',
            caption: (
              <>
                <b>Site public.</b> Rendu côté serveur avec Next.js, maquetté sur Figma avant
                développement.
              </>
            ),
          },
        ]}
      />
    </Reveal>

    <Reveal>
      <SectionHeading>Le déploiement</SectionHeading>
      <p className="cs-lead">
        Chaque push déclenche la construction des images Docker, puis une release qui progresse de{' '}
        <code>dev</code> à <code>prod</code> sur Google Kubernetes Engine, la mise en production
        restant conditionnée à une <strong>approbation manuelle</strong>.
      </p>

      <CarematePipeline />
      <Caption>Pipeline Cloud Build vers Cloud Deploy, promotion progressive entre environnements.</Caption>
    </Reveal>

    <Reveal>
      <SectionHeading>Ce que j’en retiens</SectionHeading>
      <Note>
        <p>
          <b>Avec du recul.</b> Les microservices étaient, honnêtement, plus lourds que ce que le
          produit exigeait. C’était un projet d’école, et l’objectif était d’affronter ces problèmes
          pour de vrai.
        </p>
        <p>
          C’est justement ce qui m’a appris à évaluer le coût d’une architecture avant de la choisir,
          et à préférer la solution la plus simple qui tienne la charge.
        </p>
      </Note>
    </Reveal>

    <Reveal>
      <SectionHeading>Technologies</SectionHeading>
      <TechList
        items={[
          'Node.js',
          'NestJS',
          'TypeScript',
          'Next.js',
          'RabbitMQ',
          'Socket.IO',
          'MongoDB',
          'Spring Boot',
          'Docker',
          'Kubernetes (GKE)',
          'Cloud Build',
          'CI/CD',
        ]}
      />
    </Reveal>
  </CaseStudy>
);

CarematePage.propTypes = {
  location: PropTypes.object.isRequired,
};

export default CarematePage;
